import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from "@angular/core";
import { UntypedFormControl, UntypedFormGroup } from "@angular/forms";

import { AddLocationDialogComponent } from "@components/organisations";
import { OrganisationOverlayService } from "@components/organisations/pages/organisation-overlay/organisation-overlay.service";
import { OrganisationRiskAssessmentReportRelatedRecordsService } from "@components/organisations/services";
import {
  LocationsTableComponent,
  OverlayCertificateAttachmentsComponent,
  OverlayDocumentAttachmentsComponent,
} from "@components/shared";
import { SlideOverlayContentComponent } from "@components/shared/overlay/slide-overlay-content/slide-overlay-content.component";
import { SlideOverlayMenuItemDirective } from "@components/shared/overlay/slide-overlay-menu-item/slide-overlay-menu-item.directive";
import { SlideOverlayPageClass } from "@components/shared/overlay/slide-overlay-page/slide-overlay-page.class";
import { RiskAssessmentReportRecord } from "@components/shared/risk-assessment-reports/models";
import { OverlayRiskAssessmentReportsComponent } from "@components/shared/risk-assessment-reports/ui";
import { RiskAssessmentTemplateResourceType } from "@components/shared/risk-assessment-templates/constants";
import { CopyModeEnum } from "@components/shared/value-and-copy-button/value-and-copy-button.model";
import { TextConstants } from "@shared/constants";
import {
  AttachmentTargetEnum,
  AttachmentTypeEnum,
  CustomFieldsResourceTypeEnum,
  EntityTypeEnum,
  OverlayTabEnum,
  RecordStateEnum,
  RouteEnum,
} from "@shared/enums";
import {
  IAttachment,
  ILocationDetails,
  IOrganisation,
  IOrganisationPayload,
  IRecordState,
  ISelectOption,
} from "@shared/interfaces";
import {
  AttachmentsService,
  CommonService,
  ConnectionsService,
  OrganisationsService,
} from "@shared/services";
import { CommonUtils, CustomFieldsUtils, FormUtils } from "@shared/utils";
import { CustomValidators } from "@shared/validators";

@Component({
  standalone: false,
  selector: "app-organisation-overlay",
  templateUrl: "./organisation-overlay.component.html",
  styleUrl: "../organisation-overlay-common.scss",
  changeDetection: ChangeDetectionStrategy.Default,
})
export class OrganisationOverlayComponent
  extends SlideOverlayPageClass
  implements OnInit, OnDestroy
{
  @ViewChild("locationsTable") locationsTable: LocationsTableComponent;

  @ViewChild("certificateSection") certificateSection: OverlayCertificateAttachmentsComponent;

  @ViewChild("documentSection") documentSection: OverlayDocumentAttachmentsComponent;

  @ViewChild("slideOverlayContent") override slideOverlayContent: SlideOverlayContentComponent;

  @ViewChild("riskAssessmentsView")
  readonly riskAssessmentsView: OverlayRiskAssessmentReportsComponent;

  isActiveOrganisation: boolean = false;

  formGroup: UntypedFormGroup = new UntypedFormGroup({
    name: new UntypedFormControl(null, [CustomValidators.required]),
    website: new UntypedFormControl(null, [CustomValidators.url()]),
    street: new UntypedFormControl(null),
    region: new UntypedFormControl(null),
    zipCode: new UntypedFormControl(null),
    country: new UntypedFormControl(null, [CustomValidators.required]),
  });

  selectedLocationsIds = signal<string[]>([]);

  public readonly countryOptions = computed(() => this.commonService.countriesOptions());

  public readonly copyModeEnum = CopyModeEnum;

  public isLoadingLocations = signal(true);

  override menuItems = signal(
    new Map([
      [OverlayTabEnum.DETAILS, { title: TextConstants.ORGANISATION_DETAILS, isEnabled: true }],
      [OverlayTabEnum.LOCATIONS, { title: TextConstants.LOCATIONS, isEnabled: false }],
      [OverlayTabEnum.CERTIFICATES, { title: TextConstants.CERTIFICATES, isEnabled: false }],
      [OverlayTabEnum.DOCUMENTS, { title: TextConstants.DOCUMENTS, isEnabled: false }],
      [
        OverlayTabEnum.RISK_ASSESSMENT_REPORTS,
        {
          title: TextConstants.RISK_ASSESSMENT_REPORTS,
          isEnabled: false,
          isHidden: !this.isRegularUser,
        },
      ],
      [
        OverlayTabEnum.COMMENTS,
        {
          title: TextConstants.COMMENTS,
          isEnabled: false,
          isHidden: !this.isRegularUser,
        },
      ],
    ]),
  );

  override element: IOrganisation;

  readonly attachmentTargetEnum = AttachmentTargetEnum;

  public readonly entityTypeEnum = EntityTypeEnum;

  public override entityType = EntityTypeEnum.CONNECTIONS;

  override readonly attachmentTargetType = AttachmentTargetEnum.ORGANISATION;

  override readonly deleteConfirmationContentText = $localize`:@@organisation-overlay.help:Deleting an organisation will completely remove it from the system. Any attached documents and/or certificates will remain`;

  readonly riskAssessmentReportRelatedRecords = signal<{
    data: RiskAssessmentReportRecord["record"][];
    isLoading: boolean;
  }>({ data: undefined, isLoading: undefined });

  readonly templateResourceType = RiskAssessmentTemplateResourceType.ORGANISATION;

  private activeOrganisationName: string;

  private locationAttachments: IAttachment[] = [];

  public readonly translations: any = {
    nameLabel: TextConstants.NAME,
    websiteLabel: TextConstants.WEBSITE,
    addressLabel: TextConstants.ADDRESS,
    countryLabel: TextConstants.COUNTRY,
    zipCodeLabel: TextConstants.ZIP_POSTCODE,
    regionLabel: TextConstants.STATE_REGION,
    missingTypesTp: TextConstants.MISSING_DOC_TYPES_TOOLTIP,
  };

  constructor(
    public organisationOverlay: OrganisationOverlayService,
    private connectionsService: ConnectionsService,
    private organisationsService: OrganisationsService,
    private attachmentsService: AttachmentsService,
    private commonService: CommonService,
    private cdr: ChangeDetectorRef,
    private riskAssessmentReportRelatedRecordsService: OrganisationRiskAssessmentReportRelatedRecordsService,
  ) {
    super();

    this.activeOrganisationName = this.authenticationService.getActiveOrganisation().name;
  }

  override get isSubmitButtonDisabled(): boolean {
    if (!this.isEditing()) {
      return false;
    }

    return this.formGroup?.invalid || this.formGroup?.pending || !this.hasFormValuesChanged();
  }

  loadCounters(): void {
    if (this.recordId) {
      this.loadDocuments();
      this.loadCertificates();
      this.loadComments();

      if (this.isRegularUser && this.element?.id && this.element.id !== this.activeOrganisationId) {
        this.organisationOverlay.loadReportsCounter(this.entityUri);
      }
    }
  }

  setCountersToLoadingState() {
    this.organisationOverlay.setCountersToLoadingState();
    this.selectedLocationsIds.set(null);
  }

  setCountersEmptyState() {
    this.organisationOverlay.setCountersEmptyState();
  }

  loadDocuments(): void {
    this.organisationOverlay.loadDocumentCounter(this.element.id, this.attachmentTargetType);
  }

  loadCertificates(): void {
    this.organisationOverlay.loadCertificateCounter(this.element.id, this.attachmentTargetType);
  }

  loadComments(): void {
    this.organisationOverlay.loadCommentCounter(this.entityUri);
  }

  getAttachments = async (attachmentType: AttachmentTypeEnum): Promise<IAttachment[]> => {
    return await this.attachmentsService.getAll(
      attachmentType,
      this.isActiveOrganisation
        ? `/organisations/${this.element.id}`
        : `/organisations/${this.activeOrganisationId}/connections/${this.element.id}`,
      null,
    );
  };

  async ngOnInit(): Promise<void> {
    this.overlay.showLoading();
    if (!this.isOnCorrectOverlay(RouteEnum.OVERLAY_ORGANISATION)) {
      return;
    }

    const recordId = this.recordId;

    if (recordId) {
      this.setCountersToLoadingState();
      const elementLoaded = await this.reloadElement(recordId);

      if (!elementLoaded) {
        return;
      }
      this.loadCounters();
      await this.onAfterReloadElement();
      await this.setMenuItemFromURLParam();
    } else {
      await this.setAllCustomFields(CustomFieldsResourceTypeEnum.CONNECTION);
      this.setupForm();
      this.overlay.dismissLoading();
    }
  }

  ngOnDestroy(): void {
    this.setCountersEmptyState();
    this.subscriptions.unsubscribe();
  }

  override setupForm(): void {
    let countryValue: ISelectOption;

    if (this.isEditing()) {
      countryValue = this.countryOptions().find((c) => c.value === this.element.address?.country);
    }

    this.formGroup = new UntypedFormGroup({
      name: new UntypedFormControl(
        this.element?.name ?? null,
        [CustomValidators.required],
        [
          CustomValidators.entityAlreadyExists(
            this.connectionsService,
            this.element?.id ?? this.route.snapshot.params["id"] ?? null,
          ),
        ],
      ),
      website: new UntypedFormControl(this.element?.website ?? null, [CustomValidators.url()]),
      street: new UntypedFormControl(this.element?.address?.street ?? null),
      region: new UntypedFormControl(this.element?.address?.region ?? null),
      zipCode: new UntypedFormControl(this.element?.address?.zipCode ?? null),
      country: new UntypedFormControl(countryValue, [CustomValidators.required]),
    });

    if (!this.isActiveOrganisation && this.isRegularUser) {
      this.formGroup.controls["name"].addValidators([
        CustomValidators.forbiddenValueValidator(
          this.activeOrganisationName,
          TextConstants.SAME_ORG_NAME_ERROR,
        ),
      ]);
    }

    this.visibleCustomFields = CustomFieldsUtils.getVisible(
      this.allCustomFields,
      this.element?.customFields,
    );
    CustomFieldsUtils.addToFormGroup(
      this.formGroup,
      this.visibleCustomFields,
      this.element?.customFields,
    );

    this.initialFormValue = this.formGroup.value;
    this.hasFormValuesChanged.set(false);
    this.subscriptions.add(
      this.formGroup.valueChanges.subscribe(() => {
        this.hasFormValuesChanged.set(
          !this.formGroup.pristine && this.hasInitialFormValueChanged(this.formGroup.value),
        );
      }),
    );
  }

  public override async save(): Promise<boolean> {
    if (await FormUtils.isInvalid(this.formGroup)) {
      FormUtils.findAndMarkInvalidControls(this.formGroup);
      this.notificationService.showError(TextConstants.FILL_REQUIRED_FIELDS);

      return false;
    }
    this.overlay.closeSideMenu();
    this.overlay.showLoading();

    const payload = this.getSavePayload();
    const service = this.isActiveOrganisation ? this.organisationsService : this.connectionsService;
    const id = this.element?.id;

    try {
      this.element = (await service.createOrUpdate(payload, id)) as IOrganisation;
      this.element.recordState = RecordStateEnum.ACTIVE;

      this.notificationService.showSuccess(
        this.isEditing()
          ? $localize`:@@organisation-overlay.organisationmodified:Organisation modified`
          : TextConstants.ORGANISATION_CREATED,
      );
      if (this.element.id === this.activeOrganisationId) {
        this.authenticationService.setActiveOrganisationName(payload.name);
      }

      return true;
    } catch (error) {
      this.notificationService.showError(error);

      return false;
    } finally {
      this.overlay.dismissLoading();
    }
  }

  override async afterSave(isSaveOnly: boolean): Promise<void> {
    if (isSaveOnly && !this.isEditing()) {
      await this.routerService.navigate(this.routerService.getOrganisationLink(this.element.id), {
        replaceUrl: true,
      });
    } else {
      this.reloadElement(this.element.id);
    }
  }

  async onAddLocationClicked(): Promise<void> {
    const result = await this.overlay.openDialog<
      {
        hasSaved: boolean;
        location?: ILocationDetails;
      },
      { countryOptions: ISelectOption[]; organisation: IOrganisation }
    >(AddLocationDialogComponent, {
      countryOptions: this.countryOptions(),
      organisation: this.element,
    });

    if (result?.hasSaved) {
      this.isLoadingLocations.set(true);
      this.cdr.detectChanges();
      const newLocationId = result.location.id;

      this.selectedLocationsIds.set([newLocationId, ...this.selectedLocationsIds()]);
      this.isLoadingLocations.set(false);
    }
  }

  protected override async archiveRecord(id: string, payload: IRecordState): Promise<void> {
    await this.connectionsService.setRecordState(payload, id);
  }

  protected override async deleteRecord(id: string): Promise<void> {
    await this.connectionsService.delete(id);
  }

  protected override async reloadElement(id: string): Promise<boolean> {
    try {
      this.overlay.showLoading();

      let customFieldsResourceType = CustomFieldsResourceTypeEnum.ORGANISATION;

      if (id === this.activeOrganisationId) {
        this.isActiveOrganisation = true;
        this.element = await this.organisationsService.get(this.activeOrganisationId);
        this.element.recordState = RecordStateEnum.ACTIVE;
      } else {
        this.element = await this.connectionsService.get(id);
        customFieldsResourceType = CustomFieldsResourceTypeEnum.CONNECTION;
      }

      await this.setAllCustomFields(customFieldsResourceType);
      this.setEditMode();
      this.setupForm();
      this.overlay.dismissLoading();

      return true;
    } catch (error) {
      this.notificationService.showError(error);

      return false;
    }
  }

  async onMenuItemChanged(menuItem: SlideOverlayMenuItemDirective): Promise<void> {
    if (this.isRegularUser && menuItem.tab() === OverlayTabEnum.RISK_ASSESSMENT_REPORTS) {
      await this.setRiskAssessmentReportRelatedRecords();
    }
  }

  private async setRiskAssessmentReportRelatedRecords(): Promise<void> {
    if (!this.isRegularUser) {
      return;
    }

    this.riskAssessmentReportRelatedRecords.update((state) => ({ ...state, isLoading: true }));

    const data = await this.riskAssessmentReportRelatedRecordsService.get({
      id: this.element.id,
      countryCode: this.element.address.country,
    });

    this.riskAssessmentReportRelatedRecords.set({ data, isLoading: false });
  }

  public onViewExternalProfile = async (): Promise<void> => {
    if (!this.element?.publicUri) {
      return;
    }
    const publicOrganisationId = CommonUtils.getUriId(this.element.publicUri);

    try {
      await this.organisationsService.getPublic(publicOrganisationId);
      this.routerService.navigate(
        this.routerService.getPublicOrganisationLink(publicOrganisationId),
      );
    } catch {
      this.notificationService.showError(
        $localize`:@@organisation-overlay.noexternalorginfo:Could not get external organisation information`,
      );
    }
  };

  private async onAfterReloadElement(): Promise<void> {
    this.locationAttachments = await this.getAttachments(AttachmentTypeEnum.LOCATION);
    this.selectedLocationsIds.set(await this.getSelectedLocationsIds());
    this.isLoadingLocations.set(false);
  }

  private getSelectedLocationsIds = async (): Promise<string[]> => {
    if (!this.locationAttachments?.length) {
      return [];
    }

    return this.locationAttachments.map((a) => CommonUtils.getUriId(a.attachmentUri));
  };

  private getSavePayload(): IOrganisationPayload {
    const payload: IOrganisationPayload = {
      id: this.element?.id ?? undefined,
      name: this.formGroup.controls["name"].value.trim(),
      website: FormUtils.addUrlProtocol(this.formGroup.controls["website"].value),
      address: {
        street: this.formGroup.controls["street"].value,
        region: this.formGroup.controls["region"].value,
        zipCode: this.formGroup.controls["zipCode"].value,
        country: this.formGroup.controls["country"].value.value,
      },
    };

    if (!this.isActiveOrganisation && this.element?.publicUri) {
      payload.publicUri = this.element?.publicUri;
    }

    CustomFieldsUtils.addToPayload(
      payload,
      this.activeOrganisationId,
      this.formGroup,
      this.visibleCustomFields,
    );

    return payload;
  }
}
